import React from "react";

const normalizeHref = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";

  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (v.toLowerCase().startsWith("wa:") || v.toLowerCase().startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (/^https?:\/\//i.test(v)) return v;
  if (v.toLowerCase().startsWith("www.")) return `https://${v}`;
  if (v.startsWith("/")) return v;
  return `/${v}`;
};

const goToLink = (raw) => {
  const href = normalizeHref(raw);
  if (!href) return;

  const isExternal = /^https?:\/\//i.test(href);
  if (isExternal) window.open(href, "_blank", "noopener,noreferrer");
  else window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const HeroSection1 = ({ config, style }) => {
  const direction = (config?.direction || "ltr").toLowerCase();
  const isRTL = direction === "rtl";

  const align = (config.alignment || "left").toLowerCase();
  const alignMap = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };
  const justifyMap = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
  };

  const alignClass = alignMap[align] || "text-left";
  const justifyClass = justifyMap[align] || "justify-start";

  const primaryText = (config.buttonText || "").trim();
  const primaryLink = (config.buttonLink || "").trim();
  const primaryDisabled = !!primaryText && !primaryLink;

  const secondaryText = (config.secondaryButtonText || "").trim();
  const secondaryLink = (config.secondaryButtonLink || "").trim();
  const secondaryDisabled = !!secondaryText && !secondaryLink;

  const bg = buildBg(style, "bg-gradient-to-br from-blue-50 to-white");

  const badgePositionClass = isRTL
    ? "right-3 sm:right-4 md:-right-6 text-right"
    : "left-3 sm:left-4 md:-left-6 text-left";

  return (
    <section
      dir={direction}
      style={bg.style}
      className={`min-h-screen flex items-center py-20 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className={alignClass}>
            <h1
              style={{ color: style?.titleColor || undefined }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            >
              {config.title || "Your title"}
            </h1>

            <p
              style={{ color: style?.subtitleColor || undefined }}
              className="text-xl text-gray-600 mb-8 max-w-2xl"
            >
              {config.subtitle || "Your subtitle"}
            </p>

            <div className={`flex flex-wrap gap-4 ${justifyClass}`}>
              {primaryText ? (
                <button
                  disabled={primaryDisabled}
                  onClick={() => !primaryDisabled && goToLink(primaryLink)}
                  style={{
                    backgroundColor: style?.primaryBtnBg || undefined,
                    color: style?.primaryBtnText || undefined,
                  }}
                  className={`font-semibold py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105 ${
                    primaryDisabled
                      ? "opacity-40 cursor-not-allowed"
                      : style?.primaryBtnBg
                      ? "hover:opacity-90"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {primaryText}
                </button>
              ) : null}

              {secondaryText ? (
                <button
                  disabled={secondaryDisabled}
                  onClick={() => !secondaryDisabled && goToLink(secondaryLink)}
                  style={{
                    borderColor: style?.secondaryBtnBorder || undefined,
                    color: style?.secondaryBtnText || undefined,
                  }}
                  className={`border-2 font-semibold py-3 px-8 rounded-lg transition duration-300 ${
                    secondaryDisabled
                      ? "opacity-40 cursor-not-allowed"
                      : style?.secondaryBtnBorder || style?.secondaryBtnText
                      ? "hover:opacity-90"
                      : "border-gray-300 hover:border-blue-600 text-gray-700 hover:text-blue-600"
                  }`}
                >
                  {secondaryText}
                </button>
              ) : null}
            </div>
          </div>

          <div className="relative px-1 sm:px-2 md:px-0">
            {config.image ? (
              <img
                src={config.image}
                alt={config.imageAlt || "Hero"}
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            ) : (
              <div className="rounded-2xl shadow-2xl w-full h-[320px] bg-gray-200" />
            )}

         <div
              style={{
                backgroundColor: style?.heroBadgeBg || undefined,
                borderColor: style?.heroBadgeBorder || undefined,
              }}
              className={`absolute bottom-3 sm:bottom-4 md:-bottom-6 ${badgePositionClass} p-4 sm:p-5 md:p-6 rounded-2xl shadow-xl max-w-[calc(100%-1.5rem)] sm:max-w-[calc(100%-2rem)] md:max-w-none ${
                style?.heroBadgeBg || style?.heroBadgeBorder ? "border" : "bg-white"
              }`}
            >
              <div
                style={{ color: style?.heroBadgeTitleColor || undefined }}
                className="text-2xl sm:text-3xl font-bold text-blue-600"
              >
                {config.badgeTitle || "AI-Powered"}
              </div>
              <div
                style={{ color: style?.heroBadgeSubtitleColor || undefined }}
                className="text-sm sm:text-base text-gray-600"
              >
                {config.badgeSubtitle || "Business Solutions"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection1;